/**
 * Générateur — Automatisme : factoriser par les tables (21 = 3 × 7)
 * Attendu ID : 1041 | Niveau : 5ème | Domaine : Opérations
 * Rubrique : AUTOMATISME (programme 2025) — niveau 3 Eduscol entretenu :
 * le produit doit ÉVOQUER ses facteurs comme un fait, pas se reconstruire.
 * Labels (cartes exerciseur) :
 *   - Retrouver les facteurs   (trou N2 + QCM N3 + multi N3 complet)
 *   - Dans quelles tables ?    (tables multi + table commune + défi V/F)
 *
 * ── Plan de cartes validé (2026-08-31) ─────────────────────────────────────
 * Cadrage prof : produits a = b × c, b, c ∈ {2…10} (a ≤ 100), tirage
 * UNIFORME SUR LES PRODUITS (56 et 63 sortent autant que 12) ; carrés
 * autorisés (49 = 7 × 7 : facteurs différents de 1, pas forcément distincts
 * entre eux) ; 1 n'apparaît JAMAIS en réponse attendue ; jamais de facteurs
 * premiers ni du mot « premier » (prolongement 3e). INVARIANTS :
 *   - N3 libre en saisie est IMPOSSIBLE chez nous (multi-solutions +
 *     commutativité vraie = A1) : la production reste au N2 (facteur donné,
 *     trou unique), le N3 passe par QCM et multi-select ;
 *   - pools SÉPARÉS (revue prof) : le QCM N3 tire les produits à
 *     décomposition UNIQUE, le multi ceux à ≥ 2 décompositions : jamais de
 *     multi-select à une seule bonne case ;
 *   - quota du multi ASSUMÉ (revue prof 2026-08-31) : les tables 2..10
 *     n'offrent AUCUN produit à 3 décompositions : le multi a toujours
 *     exactement 2 bonnes cases (mesuré 5000/5000). Savoir « il y en a 2 »
 *     ne dit pas lesquelles, et l'élève alterne entre exerciseurs : le
 *     méta-indice ne s'installe pas ;
 *   - convention des tables « résultats jusqu'à × 10 » : écrite dans les
 *     consignes, et une table b qui divise a avec un quotient > 10 (42 et la
 *     table de 2) n'est JAMAIS proposée en option : le bon élève ne peut pas
 *     être piégé par la convention ;
 *   - table commune par PGCD ∈ {2, 3, 5, 7} : l'unicité du diviseur commun
 *     ≥ 2 est MATHÉMATIQUE, pas conventionnelle ; m/g et n/g ≤ 10 ;
 *   - fait_faux = (b, c ± 1) d'une VRAIE décomposition : b(c±1) = a ± b ≠ a,
 *     fausseté structurelle re-vérifiée ;
 *   - défi V/F : les « non » sont les premiers de 11 à 29 (jamais 2, 3, 5, 7,
 *     entrées de table) ; formulation exacte « produit de deux entiers tous
 *     deux différents de 1 » : « 26 : oui, 2 × 13 » reste justifiable ;
 *   - toutes les vérités re-vérifiées numériquement (throw sinon).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1041;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FACTEURS = 'Retrouver les facteurs';
const L_TABLES = 'Dans quelles tables ?';

// ─── Le domaine : tous les produits des tables ────────────────────────────

/** a → décompositions {b, c} avec 2 ≤ b ≤ c ≤ 10. */
const DECOMPOSITIONS = (() => {
    const map = new Map();
    for (let b = 2; b <= 10; b++) {
        for (let c = b; c <= 10; c++) {
            const a = b * c;
            if (!map.has(a)) map.set(a, []);
            map.get(a).push({ b, c });
        }
    }
    return map;
})();
/**
 * Le nombre SACRIFIÉ aux fiches d'aide (revue prof 2026-08-31) : 15 = 3 × 5
 * illustre toutes les fiches du coup de pouce et ne sort JAMAIS en tirage
 * (l'espace de 1041 couvre toutes les tables : sans sacrifice, tout exemple
 * chiffré soufflerait des tirages courants). 25 est en plus banni des paires
 * de la table commune (la fiche cite « 15 et 25 → table de 5 »).
 * Invariant protégé par cdp1041.self.test.js.
 */
export const NOMBRE_SACRIFIE = 15;
export const SACRIFIE_COMMUNE = 25;

const PRODUITS = [...DECOMPOSITIONS.keys()].filter((a) => a !== NOMBRE_SACRIFIE);
const PRODUITS_UNIQUES = PRODUITS.filter((a) => DECOMPOSITIONS.get(a).length === 1);
const PRODUITS_MULTIPLES = PRODUITS.filter((a) => DECOMPOSITIONS.get(a).length >= 2);

/** AUTO-VÉRIFICATION d'une décomposition émise. */
const verifierDecomposition = (a, b, c) => {
    if (a !== b * c || b < 2 || b > 10 || c < 2 || c > 10) {
        throw new Error(`gen_1041 : décomposition invalide ${a} = ${b} × ${c}`);
    }
};

/** Tables STRICTES de a (résultats jusqu'à × 10) : {b : b×k = a, k ≤ 10}. */
const tablesDe = (a) => {
    const t = [];
    for (let b = 2; b <= 10; b++) if (a % b === 0 && a / b <= 10) t.push(b);
    return t;
};

/** Tables INTERDITES en option : b divise a mais le quotient dépasse 10
 *  (la convention scolaire piègerait un élève mathématiquement juste). */
const tablesInterdites = (a) => {
    const t = [];
    for (let b = 2; b <= 10; b++) if (a % b === 0 && a / b > 10) t.push(b);
    return t;
};

const pgcd = (x, y) => (y === 0 ? x : pgcd(y, x % y));

// ─── Carte 1, format A : facteur manquant (N2, saisie à trou) ─────────────

const genFacteurTrous = () => {
    const as = shuffle(PRODUITS).slice(0, 3);
    const lignes = as.map((a) => {
        const { b, c } = randomChoice(DECOMPOSITIONS.get(a));
        verifierDecomposition(a, b, c);
        // Le facteur donné et le trou alternent de côté ; le trou est UNIQUE
        // mathématiquement (a / donné), même si a a plusieurs décompositions.
        const trouAGauche = randomChoice([true, false]);
        const [donne, attendu] = randomChoice([[b, c], [c, b]]);
        return trouAGauche
            ? `$${a} = {{${attendu}}} \\times ${donne}$`
            : `$${a} = ${donne} \\times {{${attendu}}}$`;
    });
    const payload = makeTexteTrou(
        lignes.join('\n'),
        '<p><b>Complète chaque égalité avec le facteur qui manque.</b></p>',
        { ...opts(L_FACTEURS), mode: 'saisie' },
    );
    // Feedback DORMANT par motif (pas de message immédiat sans toucher au
    // rendu élève ; l'UI future routera par clé d'erreur).
    payload.contenu = {
        ...payload.contenu,
        explications: { reponse_egale_produit: 'Le trou attend le facteur qui manque, pas le nombre de départ.' },
    };
    payload._meta.params = { produits: as };
    return payload;
};

// ─── Carte 1, format B : QCM décomposition (N3, produits uniques) ─────────

/** Feedback DORMANT des QCM, par motif : la version « après » de la fiche
 *  d'aide « Vérifier une écriture » (revue prof 2026-08-31). */
const EXPLICATIONS_QCM = {
    un_fois_n: '1 × le nombre ne compte pas : les deux facteurs doivent être différents de 1.',
    somme: 'C\'est une somme, pas un produit : une décomposition s\'écrit avec un signe ×.',
    fait_faux_voisin: 'Refais la multiplication : le résultat ne donne pas le nombre demandé.',
};

/** Écritures distractrices, chacune une misconception nommée. */
const fabriquerFaux = (a, b, c) => {
    // fait_faux : voisin de table (b, c ± 1) : vaut a ± b, jamais a.
    const delta = c >= 10 ? -1 : c <= 2 ? 1 : randomChoice([-1, 1]);
    const faitFaux = { texte: tex(`${b} \\times ${c + delta}`), code: 'fait_faux_voisin', valeur: b * (c + delta) };
    if (faitFaux.valeur === a) throw new Error(`gen_1041 : fait_faux vaut a (${a})`);
    // somme : deux termes ≥ 2 qui font a : la confusion facteur/terme.
    const s = randInt(2, a - 2);
    const somme = { texte: tex(`${s} + ${a - s}`), code: 'somme', valeur: a };
    // un_fois_n : la réponse-refuge, rejetée par la consigne elle-même.
    const unFoisN = { texte: tex(`1 \\times ${a}`), code: 'un_fois_n', valeur: a };
    return [faitFaux, somme, unFoisN];
};

const genDecompositionQcm = () => {
    const a = randomChoice(PRODUITS_UNIQUES);
    const { b, c } = DECOMPOSITIONS.get(a)[0];
    verifierDecomposition(a, b, c);
    const bonne = tex(`${b} \\times ${c}`);
    const faux = fabriquerFaux(a, b, c);
    const payload = makeQcm(
        [bonne, ...faux.map((x) => x.texte)],
        bonne,
        `<p><b>Quelle écriture décompose ${a} en un produit de deux facteurs différents de 1 ?</b></p>`,
        opts(L_FACTEURS),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS_QCM };
    payload._meta.params = { a, b, c, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 1, format C : multi-select (N3 complet, produits multiples) ────

const genDecompositionsMulti = () => {
    const a = randomChoice(PRODUITS_MULTIPLES);
    const decomps = DECOMPOSITIONS.get(a);
    decomps.forEach(({ b, c }) => verifierDecomposition(a, b, c));
    const bonnes = decomps.map(({ b, c }) => tex(`${b} \\times ${c}`));
    const { b, c } = randomChoice(decomps);
    const faux = fabriquerFaux(a, b, c);
    const payload = makeQcm(
        [...bonnes, ...faux.map((x) => x.texte)],
        bonnes,
        // « Donne toutes » et pas « Coche toutes » : en print PDF mode libre,
        // les cases disparaissent et la consigne doit garder son sens.
        `<p><b>Donne toutes les écritures qui décomposent ${a} en un produit de deux facteurs différents de 1.</b></p>`,
        opts(L_FACTEURS),
        true,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS_QCM };
    payload._meta.params = { a, nbBonnes: bonnes.length, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2, format A : dans quelles tables ? (multi) ────────────────────

const genTablesMulti = () => {
    // Un produit dont l'ensemble des tables strictes compte 2 à 4 entrées.
    let a = randomChoice(PRODUITS);
    for (let essai = 0; essai < 60; essai++) {
        const n = tablesDe(a).length;
        if (n >= 2 && n <= 4) break;
        a = randomChoice(PRODUITS);
    }
    const bonnes = tablesDe(a);
    const interdites = tablesInterdites(a);
    // Distracteurs : des tables qui ne divisent PAS a : jamais la zone grise.
    const candidates = [];
    for (let t = 2; t <= 10; t++) {
        if (!bonnes.includes(t) && !interdites.includes(t)) candidates.push(t);
    }
    const distracteurs = shuffle(candidates).slice(0, Math.max(2, 5 - bonnes.length));
    const options = shuffle([...bonnes, ...distracteurs]);
    // AUTO-VÉRIFICATION : aucune option interdite, chaque bonne re-vérifiée.
    options.forEach((t) => {
        if (interdites.includes(t)) throw new Error(`gen_1041 : table ambiguë ${t} proposée pour ${a}`);
    });
    bonnes.forEach((t) => {
        if (a % t !== 0 || a / t > 10) throw new Error(`gen_1041 : table ${t} fausse pour ${a}`);
    });
    const libelle = (t) => `table de ${t}`;
    const payload = makeQcm(
        options.map(libelle),
        bonnes.map(libelle),
        `<p><b>Dans quelles tables de multiplication (résultats jusqu'à × 10) trouve-t-on ${a} ?</b></p>`
        + '<p><b>Donne-les toutes.</b></p>',
        opts(L_TABLES),
        true,
    );
    payload._meta.params = { a, bonnes, interdites };
    return payload;
};

// ─── Carte 2, format B : la table commune (saisie) ────────────────────────

const genTableCommune = () => {
    // PGCD premier ∈ {2, 3, 5, 7} : unicité MATHÉMATIQUE du diviseur commun
    // ≥ 2 ; m/g, n/g ≤ 10 : la convention des tables tient des deux côtés.
    for (let essai = 0; essai < 80; essai++) {
        const g = randomChoice([2, 3, 5, 7]);
        const m1 = randInt(2, 10);
        const n1 = randInt(2, 10);
        if (m1 === n1 || pgcd(m1, n1) !== 1) continue;
        const m = g * m1;
        const n = g * n1;
        // Les nombres des fiches d'aide ne sortent jamais ici (15 ET 25).
        if ([m, n].some((v) => v === NOMBRE_SACRIFIE || v === SACRIFIE_COMMUNE)) continue;
        if (pgcd(m, n) !== g) continue;
        // AUTO-VÉRIFICATION : la table commune stricte est exactement {g}.
        const communes = tablesDe(m).filter((t) => tablesDe(n).includes(t));
        if (communes.length !== 1 || communes[0] !== g) continue;
        const payload = makeSaisie(
            String(g),
            `<p><b>${m} et ${n} se trouvent tous les deux dans une même table de multiplication (résultats jusqu'à × 10).</b></p>`
            + '<p><b>Laquelle ? Donne le nombre.</b></p>',
            opts(L_TABLES),
        );
        payload._meta.params = { m, n, g };
        return payload;
    }
    // Repli déterministe : l'exemple canonique 21 / 35 → table de 7.
    const payload = makeSaisie(
        '7',
        '<p><b>21 et 35 se trouvent tous les deux dans une même table de multiplication (résultats jusqu\'à × 10).</b></p>'
        + '<p><b>Laquelle ? Donne le nombre.</b></p>',
        opts(L_TABLES),
    );
    payload._meta.params = { m: 21, n: 35, g: 7 };
    return payload;
};

// ─── Carte 2, format C : le défi « décomposable ? » (V/F) ─────────────────

/** « non » : premiers de 11 à 29 (jamais les entrées de table 2, 3, 5, 7) ;
 *  « oui » : composés ≤ 30, des tables ou décidables par parité (2 × 13). */
const POOL_NON = [11, 13, 17, 19, 23, 29];
// 15 exclu : sacrifié aux fiches d'aide (cf. NOMBRE_SACRIFIE).
const POOL_OUI = [12, 14, 16, 18, 20, 21, 22, 24, 25, 26, 27, 28];

const estComposable = (n) => {
    for (let d = 2; d < n; d++) if (n % d === 0) return true;
    return false;
};

const genDecomposableVf = () => {
    const nbOui = randInt(1, 2);
    const nombres = [
        ...shuffle(POOL_OUI).slice(0, nbOui),
        ...shuffle(POOL_NON).slice(0, 3 - nbOui),
    ];
    const lignes = [];
    const bonnes = [];
    const explications = [];
    shuffle(nombres).forEach((n) => {
        const composable = estComposable(n);
        // AUTO-VÉRIFICATION : l'étiquette du pool concorde avec la primalité.
        if (composable !== POOL_OUI.includes(n)) {
            throw new Error(`gen_1041 : ${n} mal classé dans les pools V/F`);
        }
        lignes.push(`${n} peut s'écrire comme un produit de deux entiers tous deux différents de 1`);
        bonnes.push(composable ? 0 : 1);
        // Feedback DORMANT par ligne : la décomposition trouvée, ou le refus
        // par les tables : jamais le mot de la notion (prolongement 3e).
        if (composable) {
            let d = 2;
            while (n % d !== 0) d++;
            explications.push(`${n} = ${d} × ${n / d}`);
        } else {
            explications.push(`Aucune table de 2 à 10 ne donne ${n}.`);
        }
    });
    const payload = makeVraiFaux(lignes, bonnes, '<p><b>Vrai ou faux ?</b></p>', opts(L_TABLES));
    payload.contenu = { ...payload.contenu, explications };
    return payload;
};

// ─── Contrat distracteurs (référence de revue, formats QCM) ───────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève, jamais du bruit.
 * Arbitrage prof (décideur didactique), 2026-08-31.
 */
export const REGLES_DISTRACTEURS_QCM = {
    decomposition_qcm_et_multi: {
        support: 'écritures candidates pour un produit des tables',
        fixes: {
            un_fois_n: '1 × a : la réponse-refuge, vraie arithmétiquement mais rejetée par la consigne « différents de 1 » : LA règle de l\'attendu',
            somme: 's + t = a : confusion facteur/terme (objectif 5e « distinguer sommes et produits »)',
            fait_faux_voisin: 'b × (c ± 1) : fait de table erroné d\'un cran, vaut a ± b, jamais a (fausseté structurelle)',
        },
    },
    tables_qcm_multi: {
        support: 'tables de 2 à 10 ; convention « résultats jusqu\'à × 10 » écrite dans la consigne',
        interdit: 'une table qui divise a avec un quotient > 10 (42 et la table de 2) : l\'élève qui la cocherait aurait mathématiquement raison : jamais en option',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    facteur_texte_trou_saisie: genFacteurTrous,
    decomposition_qcm: genDecompositionQcm,
    decompositions_qcm_multi: genDecompositionsMulti,
    tables_qcm_multi: genTablesMulti,
    table_commune_saisie: genTableCommune,
    decomposable_vf: genDecomposableVf,
};

export const CATEGORIES = [
    { id: 'facteurs', label: L_FACTEURS, formatsList: [
        { key: 'facteur_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'decomposition_qcm', label: 'QCM' },
        { key: 'decompositions_qcm_multi', label: 'QCM multiple' },
    ] },
    { id: 'tables', label: L_TABLES, formatsList: [
        { key: 'tables_qcm_multi', label: 'QCM multiple' },
        { key: 'table_commune_saisie', label: 'Saisie' },
        { key: 'decomposable_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
